import React, { useEffect, useMemo, useState } from "react";
import { AppShell } from "@/components/AppShell";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Loader2, Search, BrainCircuit, Pencil, X, Check, RotateCcw, Filter,
  ChevronDown, ShieldCheck, ShieldAlert, AlertTriangle, ListChecks,
} from "lucide-react";

// --- Static filter options (groups stay; root-cause/recoverability come from API) ---
const GROUPS = [
  { id: "", label: "All Groups" },
  { id: "CO", label: "CO · Contractual" },
  { id: "PR", label: "PR · Patient Resp." },
  { id: "OA", label: "OA · Other Adjustment" },
  { id: "PI", label: "PI · Payer Initiated" },
];

// --- Visual chip helpers — every cell is its own micro-component for clarity ---
function Chip({ tone, children, testid }) {
  const tones = {
    teal:    "bg-teal-50 text-teal-800 border-teal-200",
    amber:   "bg-amber-50 text-amber-800 border-amber-200",
    coral:   "bg-rose-50 text-rose-800 border-rose-200",
    green:   "bg-emerald-50 text-emerald-800 border-emerald-200",
    slate:   "bg-slate-100 text-slate-700 border-slate-200",
    indigo:  "bg-indigo-50 text-indigo-800 border-indigo-200",
  };
  return (
    <span
      data-testid={testid}
      className={`inline-flex items-center text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-md border ${tones[tone] || tones.slate}`}
    >
      {children}
    </span>
  );
}

function RecoverabilityChip({ value }) {
  const map = { High: "green", Medium: "amber", Low: "coral" };
  return <Chip tone={map[value] || "slate"}>{value || "—"}</Chip>;
}

function PriorityChip({ value, score }) {
  const map = { High: "coral", Medium: "amber", Low: "green" };
  return (
    <span className="inline-flex items-center gap-1.5">
      <Chip tone={map[value] || "slate"}>{value || "—"}</Chip>
      <span className="text-[11px] tabular-nums text-slate-500">{score}</span>
    </span>
  );
}

function AppealChip({ eligible }) {
  if (eligible) return <Chip tone="teal"><ShieldCheck className="h-2.5 w-2.5 mr-1" />Eligible</Chip>;
  return <Chip tone="slate"><ShieldAlert className="h-2.5 w-2.5 mr-1" />Not appealable</Chip>;
}

function IndicatorDot({ tone }) {
  const c = { red: "bg-rose-500", amber: "bg-amber-400", green: "bg-emerald-500", blue: "bg-slate-400" };
  return <span className={`inline-block h-2 w-2 rounded-full ${c[tone] || c.blue}`} />;
}

export default function AnsiCodeLibraryPage() {
  const [items, setItems] = useState([]);
  const [filterOpts, setFilterOpts] = useState({ root_causes: [], recoverability: [], priorities: [] });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [group, setGroup] = useState("");
  const [rootCause, setRootCause] = useState("");
  const [recoverability, setRecoverability] = useState("");
  const [priority, setPriority] = useState("");
  const [appealOnly, setAppealOnly] = useState(false);
  const [editing, setEditing] = useState(null); // {display, value}
  const [busy, setBusy] = useState(false);
  const [expanded, setExpanded] = useState(null); // display key

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.set("search", search);
      if (group) params.set("group", group);
      if (rootCause) params.set("root_cause", rootCause);
      if (recoverability) params.set("recoverability", recoverability);
      if (priority) params.set("priority", priority);
      if (appealOnly) params.set("appeal_eligible", "true");
      const { data } = await api.get(`/ansi/library?${params.toString()}`);
      setItems(data.items || []);
      if (data.filters) setFilterOpts(data.filters);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load ANSI library");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const t = setTimeout(load, 200);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, group, rootCause, recoverability, priority, appealOnly]);

  const startEdit = (it) => setEditing({ display: it.display, value: it.short_label || "" });
  const cancelEdit = () => setEditing(null);

  const saveEdit = async () => {
    if (!editing) return;
    const label = editing.value.trim();
    if (!label) { toast.error("Label cannot be empty."); return; }
    setBusy(true);
    try {
      await api.put(`/ansi/shorthand/${encodeURIComponent(editing.display)}`, { label });
      toast.success(`Saved override for ${editing.display}`);
      setEditing(null);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  const resetOverride = async (display) => {
    setBusy(true);
    try {
      await api.delete(`/ansi/shorthand/${encodeURIComponent(display)}`);
      toast.success(`Reset ${display} to default shorthand`);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Reset failed");
    } finally { setBusy(false); }
  };

  const counts = useMemo(() => {
    const c = { total: items.length, appealable: 0, highRecov: 0, highPri: 0 };
    for (const it of items) {
      if (it.appeal_eligible) c.appealable += 1;
      if (it.recoverability === "High") c.highRecov += 1;
      if (it.priority === "High") c.highPri += 1;
    }
    return c;
  }, [items]);

  const clearAll = () => {
    setSearch(""); setGroup(""); setRootCause(""); setRecoverability(""); setPriority(""); setAppealOnly(false);
  };

  return (
    <AppShell>
      <div className="space-y-5 fade-up" data-testid="ansi-library-page">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-4">
          <div>
            <span className="label-overline inline-flex items-center gap-1.5">
              <BrainCircuit className="h-3.5 w-3.5" />
              Denial Intelligence Engine
            </span>
            <h1 className="font-display text-3xl sm:text-4xl font-semibold text-slate-900 mt-1.5 tracking-tight">
              ANSI Denial Code Library
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Every CARC translated into operational intent — root cause, recoverability, recommended action, and appeal eligibility.
            </p>
          </div>
          <div className="grid grid-cols-3 gap-3 lg:flex lg:items-center">
            <Stat label="Codes" value={counts.total} tone="slate" testid="intel-stat-total" />
            <Stat label="High Recovery" value={counts.highRecov} tone="green" testid="intel-stat-high-recov" />
            <Stat label="Appealable" value={counts.appealable} tone="teal" testid="intel-stat-appealable" />
          </div>
        </div>

        {/* Search + Filter bar */}
        <div className="surface-card p-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-[1.5fr_1fr_1fr_1fr_auto] gap-2.5">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <input
                type="text"
                data-testid="ansi-library-search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search code, description, or shorthand..."
                className="w-full pl-10 h-10 border border-slate-300 rounded-lg bg-white text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
              />
            </div>
            <FilterSelect
              testid="ansi-library-group-filter"
              icon={<Filter className="h-4 w-4 text-slate-400" />}
              value={group}
              onChange={setGroup}
              options={GROUPS}
            />
            <FilterSelect
              testid="ansi-library-root-cause-filter"
              icon={<ListChecks className="h-4 w-4 text-slate-400" />}
              value={rootCause}
              onChange={setRootCause}
              options={[{ id: "", label: "All Root Causes" }, ...filterOpts.root_causes.map((c) => ({ id: c, label: c }))]}
            />
            <FilterSelect
              testid="ansi-library-recoverability-filter"
              icon={<ShieldCheck className="h-4 w-4 text-slate-400" />}
              value={recoverability}
              onChange={setRecoverability}
              options={[{ id: "", label: "All Recoverability" }, ...filterOpts.recoverability.map((c) => ({ id: c, label: c }))]}
            />
            <Button
              variant="outline"
              data-testid="ansi-library-clear-filters"
              onClick={clearAll}
              className="h-10 text-xs border-slate-300 text-slate-700 hover:bg-slate-50"
            >
              Clear
            </Button>
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-2 text-[11px]">
            <button
              data-testid="ansi-library-appeal-only-toggle"
              onClick={() => setAppealOnly((v) => !v)}
              className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md border transition-colors ${
                appealOnly
                  ? "bg-teal-500 text-white border-teal-500"
                  : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"
              }`}
            >
              <ShieldCheck className="h-3 w-3" />
              Appealable only
            </button>
            {filterOpts.priorities.map((p) => (
              <button
                key={p}
                data-testid={`ansi-library-priority-${p.toLowerCase()}`}
                onClick={() => setPriority(priority === p ? "" : p)}
                className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md border transition-colors ${
                  priority === p
                    ? "bg-slate-900 text-white border-slate-900"
                    : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"
                }`}
              >
                {p} priority
              </button>
            ))}
          </div>
        </div>

        {/* Table */}
        <div className="surface-card overflow-hidden">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-20 text-slate-500">
              <Loader2 className="h-8 w-8 animate-spin text-teal-600" />
              <div className="mt-3 text-sm">Loading denial intelligence...</div>
            </div>
          ) : items.length === 0 ? (
            <div className="py-20 text-center text-sm text-slate-500" data-testid="ansi-library-empty">
              <AlertTriangle className="h-6 w-6 mx-auto mb-2 text-amber-500" />
              No codes match your filters.
            </div>
          ) : (
            <div className="overflow-x-auto" data-testid="ansi-library-table">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 sticky top-0">
                  <tr className="text-left text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
                    <th className="py-3 px-3 w-8"></th>
                    <th className="py-3 px-3 w-24">Code</th>
                    <th className="py-3 px-3 w-44">Operational Meaning</th>
                    <th className="py-3 px-3 w-36">Root Cause</th>
                    <th className="py-3 px-3 w-28">Recoverability</th>
                    <th className="py-3 px-3 w-36">Appeal</th>
                    <th className="py-3 px-3 w-32">Priority</th>
                    <th className="py-3 px-3 w-24 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((it) => {
                    const isOpen = expanded === it.display;
                    const isEditing = editing && editing.display === it.display;
                    return (
                      <React.Fragment key={it.display}>
                        <tr
                          data-testid={`ansi-row-${it.display}`}
                          className="border-b border-slate-100 hover:bg-slate-50/60 align-top cursor-pointer"
                          onClick={() => !isEditing && setExpanded(isOpen ? null : it.display)}
                        >
                          <td className="py-3 px-3">
                            <div className="flex items-center gap-1.5">
                              <IndicatorDot tone={it.indicator} />
                              <ChevronDown
                                className={`h-3.5 w-3.5 text-slate-400 transition-transform ${isOpen ? "rotate-180" : ""}`}
                              />
                            </div>
                          </td>
                          <td className="py-3 px-3">
                            <span className="inline-flex items-center font-mono font-bold text-[12px] px-2 py-1 rounded bg-teal-50 text-teal-800 border border-teal-200">
                              {it.display}
                            </span>
                          </td>
                          <td className="py-3 px-3" onClick={(e) => e.stopPropagation()}>
                            {isEditing ? (
                              <div className="flex items-center gap-1">
                                <input
                                  type="text"
                                  maxLength={40}
                                  autoFocus
                                  value={editing.value}
                                  onChange={(e) => setEditing({ ...editing, value: e.target.value })}
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter") saveEdit();
                                    if (e.key === "Escape") cancelEdit();
                                  }}
                                  data-testid={`ansi-edit-input-${it.display}`}
                                  className="flex-1 min-w-0 h-8 px-2 text-sm font-semibold border border-teal-400 rounded focus:outline-none focus:ring-2 focus:ring-teal-300"
                                  placeholder="e.g. Auth Absent"
                                />
                                <button
                                  data-testid={`ansi-edit-save-${it.display}`}
                                  onClick={saveEdit}
                                  disabled={busy}
                                  className="h-8 w-8 inline-flex items-center justify-center rounded bg-teal-500 hover:bg-teal-400 text-white disabled:opacity-50"
                                  title="Save"
                                >
                                  <Check className="h-4 w-4" />
                                </button>
                                <button
                                  onClick={cancelEdit}
                                  className="h-8 w-8 inline-flex items-center justify-center rounded bg-slate-200 hover:bg-slate-300 text-slate-700"
                                  title="Cancel"
                                >
                                  <X className="h-4 w-4" />
                                </button>
                              </div>
                            ) : (
                              <div className="flex items-center gap-2">
                                <span className={`text-sm font-semibold ${it.short_label ? "text-slate-900" : "text-slate-500"}`}>
                                  {it.operational_meaning || "—"}
                                </span>
                                {it.is_override && (
                                  <Chip tone="teal" testid={`ansi-custom-tag-${it.display}`}>Custom</Chip>
                                )}
                              </div>
                            )}
                          </td>
                          <td className="py-3 px-3">
                            <Chip tone="indigo" testid={`ansi-rootcause-${it.display}`}>{it.root_cause}</Chip>
                          </td>
                          <td className="py-3 px-3">
                            <RecoverabilityChip value={it.recoverability} />
                          </td>
                          <td className="py-3 px-3">
                            <AppealChip eligible={it.appeal_eligible} />
                          </td>
                          <td className="py-3 px-3">
                            <PriorityChip value={it.priority} score={it.priority_score} />
                          </td>
                          <td className="py-3 px-3 text-right whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                            {!isEditing && (
                              <div className="inline-flex items-center gap-1">
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  data-testid={`ansi-edit-${it.display}`}
                                  onClick={() => startEdit(it)}
                                  className="h-8 px-2 text-xs text-teal-700 hover:bg-teal-50"
                                  title="Edit shorthand"
                                >
                                  <Pencil className="h-3.5 w-3.5" />
                                </Button>
                                {it.is_override && (
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    data-testid={`ansi-reset-${it.display}`}
                                    onClick={() => resetOverride(it.display)}
                                    disabled={busy}
                                    className="h-8 px-2 text-xs text-slate-500 hover:bg-slate-100"
                                    title="Reset to default"
                                  >
                                    <RotateCcw className="h-3.5 w-3.5" />
                                  </Button>
                                )}
                              </div>
                            )}
                          </td>
                        </tr>
                        {isOpen && (
                          <tr className="border-b border-slate-100 bg-slate-50/40" data-testid={`ansi-row-detail-${it.display}`}>
                            <td colSpan={8} className="py-4 px-6">
                              <div className="grid md:grid-cols-2 gap-4">
                                <div>
                                  <div className="label-overline mb-1.5">Official ANSI Description</div>
                                  <p className="text-[13px] text-slate-700 leading-relaxed">{it.description}</p>
                                </div>
                                <div>
                                  <div className="label-overline mb-1.5">Recommended Action</div>
                                  <p className="text-[13px] text-slate-700 leading-relaxed">{it.recommended_action}</p>
                                  <div className="mt-2.5 flex items-center gap-1.5 text-[11px] text-slate-500">
                                    <span>Recovery likelihood:</span>
                                    <span className="font-semibold text-slate-800 tabular-nums">{it.recovery_likelihood}%</span>
                                    <span className="mx-2">·</span>
                                    <span>Driver:</span>
                                    <span className="font-semibold text-slate-800">{it.driver_label}</span>
                                  </div>
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <p className="text-xs text-slate-500">
          Each code is mapped to its operational driver, root cause, recoverability, and recommended action so the library doubles as an analyst playbook.
          Click any row to expand the official description and recommended action. Executive shorthand labels are customizable per user (max 40 chars).
        </p>
      </div>
    </AppShell>
  );
}

function Stat({ label, value, tone = "slate", testid }) {
  const tones = {
    slate: "border-slate-200 text-slate-700",
    green: "border-emerald-200 text-emerald-700",
    teal: "border-teal-200 text-teal-700",
  };
  return (
    <div
      data-testid={testid}
      className={`px-3.5 py-2 rounded-lg border ${tones[tone] || tones.slate} bg-white`}
    >
      <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</div>
      <div className="text-xl font-semibold tabular-nums">{value}</div>
    </div>
  );
}

function FilterSelect({ icon, value, onChange, options, testid }) {
  return (
    <div className="relative">
      <span className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none">{icon}</span>
      <select
        data-testid={testid}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full pl-10 pr-3 h-10 border border-slate-300 rounded-lg bg-white text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 appearance-none"
      >
        {options.map((o) => (
          <option key={o.id} value={o.id}>{o.label}</option>
        ))}
      </select>
    </div>
  );
}
